"use client";

import { useState, type ReactNode } from "react";
import { Knopf } from "../knopf";

/**
 * Dokumentansicht (Design-Guide `dokument-ansicht.md`, Betreiber 22.10.2026 „Empfehlung + 3, 5, 10, 12“, Musterkatalog M-122): links eine
 * Seitenleiste mit Miniaturen, daneben die gewählte Seite mit Markierungen. Gilt für drei Ansichten desselben Layouts: Vorprüfung (V-C),
 * Lauf live (L-A) und Ergebnis (E-A). Der Baustein zeigt nur; WAS markiert wird und welche Seite gilt, entscheidet der Aufrufer
 * (kontrolliert über `aktiveSeite`/`beiSeite`), den Zoom verwaltet er selbst.
 *
 * Markierungen sind SVG-Formen über dem Seitenbild; Lage und Größe stehen als Attribute (`x`, `y`, `width`, `height`, normiert 0 bis 1,
 * hier in die Seitenmaße umgerechnet), nie als `style=`. Farben nur Theme-Tokens. Arten: `liest` (gestrichelter Rahmen „liest gerade“,
 * sanft pulsierend nur mit `motion-safe`), `fund` (gefundenes Feld, optional nummeriert), `gewaehlt` (das gewählte Feld), `bereich` (erkannter
 * Bereich der Vorprüfung, Ton KI oder neutral). Bewegung ist nie die einzige Aussage: der Aufrufer liefert den Satz (`satz`, aria-live).
 *
 * Das Seitenbild kommt vom Server (Seitenbild-Route, nur mit Leserecht, Personenbereiche dort grau abgedeckt, Idee 12); fehlt es oder
 * ist es nicht mehr verfügbar (Laufende + 24 h), zeigt der Baustein den Satz `seiteLeerSatz` statt der Seite und die Seitenleiste bleibt
 * als Liste. Unter `lg` blendet die Seite den Baustein aus (`hidden lg:block` am Aufrufer): dort gilt die Rückfall-Ansicht ohne Dokument.
 */

export interface DokumentReiter {
  id: string;
  titel: string;
  /** Kurzangabe hinter dem Titel („PDF · 4 S.“). */
  zusatz?: string;
}

export type DokumentSeiteStand = "offen" | "laeuft" | "fertig" | "fehler";

export interface DokumentSeite {
  nr: number;
  /** Adresse des Seitenbilds; `null` = es gibt keines (nie abgelegt, abgelaufen, verworfen). */
  bildUrl: string | null;
  stand: DokumentSeiteStand;
  /** Text für Hilfstechnik und Tooltip der Miniatur („Seite 3, wird gelesen“). */
  beschriftung: string;
}

export type MarkierungArt = "liest" | "fund" | "gewaehlt" | "bereich" | "bereich-ki";

export interface DokumentMarkierung {
  id: string;
  art: MarkierungArt;
  /** Normiert 0 bis 1 (x, y = oben links; b = Breite, h = Höhe). */
  x: number;
  y: number;
  b: number;
  h: number;
  /** Nummer der Fundstelle (nur `fund`/`gewaehlt`). */
  nummer?: number;
  /** Beschriftung für Hilfstechnik („Fundstelle 3: Linie“); Pflicht, wenn die Markierung bedienbar ist. */
  label?: string;
  /** Kopfzeile des Bereichs („Formular mit Handschrift“, „liest gerade“); nur `liest`/`bereich`/`bereich-ki`. */
  titel?: string;
}

const ZOOM_STUFEN = [100, 125, 150, 200] as const;
/** Feste, vollständige Ketten je Zoomstufe (nie zusammengesetzt): die Seite wächst in die Breite, die Fläche scrollt. */
const ZOOM_SEITE: Record<(typeof ZOOM_STUFEN)[number], string> = {
  100: "relative mx-auto shadow-xs rounded-sm overflow-hidden bg-layer w-full",
  125: "relative mx-auto shadow-xs rounded-sm overflow-hidden bg-layer w-5/4",
  150: "relative mx-auto shadow-xs rounded-sm overflow-hidden bg-layer w-3/2",
  200: "relative mx-auto shadow-xs rounded-sm overflow-hidden bg-layer w-2/1",
};

const REITER_AKTIV = "px-3 py-1.5 text-sm rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus bg-layer text-foreground shadow-2xs font-medium";
const REITER_INAKTIV = "px-3 py-1.5 text-sm rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus text-muted-foreground-1 hover:text-foreground";
const MINIATUR_AKTIV = "relative block rounded-md border overflow-hidden bg-surface group-focus-visible:ring-2 group-focus-visible:ring-primary-focus border-primary ring-2 ring-primary";
const MINIATUR_INAKTIV = "relative block rounded-md border overflow-hidden bg-surface group-focus-visible:ring-2 group-focus-visible:ring-primary-focus border-layer-line";
const SEITENZAHL_AKTIV = "mt-1 block text-xs font-medium text-primary";
const SEITENZAHL_INAKTIV = "mt-1 block text-xs text-muted-foreground-1";

/** Seitenhöhe in Einheiten bei Breite 100; A4 hochkant, bis das Bild die echten Maße meldet. */
const A4_HOEHE = 141.4;

const SVG_KOPF = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};
const SYMBOL = "shrink-0 size-4";

export type AnzeigeDrehung = 0 | 90 | 180 | 270;

/**
 * Transform der Gruppe aus Bild und Markierungen für die Anzeigedrehung. Die Gruppe wird in ihren eigenen Maßen gezeichnet (100 breit,
 * `hoehe` hoch) und so gedreht und verschoben, dass sie die Fläche des SVG ausfüllt (bei 90 und 270 Grad hat das SVG die Maße
 * `hoehe` mal 100). Rechnung: 90 Grad = (x, y) → (hoehe − y, x); 180 = (100 − x, hoehe − y); 270 = (y, 100 − x).
 */
export function anzeigeTransform(grad: AnzeigeDrehung, hoehe: number): string | undefined {
  switch (grad) {
    case 90:
      return `translate(${hoehe} 0) rotate(90)`;
    case 180:
      return `translate(100 ${hoehe}) rotate(180)`;
    case 270:
      return `translate(0 100) rotate(270)`;
    default:
      return undefined;
  }
}

/** Lucide `rotate-cw` (im Uhrzeigersinn drehen). */
function SymbolDrehen() {
  return (
    <svg className={SYMBOL} {...SVG_KOPF}>
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

function SymbolLinks() {
  return (
    <svg className={SYMBOL} {...SVG_KOPF}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
function SymbolRechts() {
  return (
    <svg className={SYMBOL} {...SVG_KOPF}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
function SymbolPlus() {
  return (
    <svg className={SYMBOL} {...SVG_KOPF}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="11" x2="11" y1="8" y2="14" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </svg>
  );
}
function SymbolMinus() {
  return (
    <svg className={SYMBOL} {...SVG_KOPF}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </svg>
  );
}

/** Zustandszeichen der Miniatur: Haken (fertig), Drehsymbol (läuft, nur mit `motion-safe` bewegt), Ausrufezeichen (Fehler). */
function Stand({ stand }: { stand: DokumentSeiteStand }) {
  if (stand === "offen") return null;
  if (stand === "fertig") {
    return (
      <span className="absolute -bottom-1 -end-1 size-5 inline-flex justify-center items-center rounded-full bg-status-ok text-primary-foreground" aria-hidden="true">
        <svg className="size-3" {...SVG_KOPF} strokeWidth={3}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
    );
  }
  if (stand === "laeuft") {
    return (
      <span className="absolute -bottom-1 -end-1 size-5 inline-flex justify-center items-center rounded-full bg-primary text-primary-foreground" aria-hidden="true">
        <svg className="size-3 motion-safe:animate-spin" {...SVG_KOPF}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      </span>
    );
  }
  return (
    <span className="absolute -bottom-1 -end-1 size-5 inline-flex justify-center items-center rounded-full bg-status-warn text-primary-foreground text-xs font-bold" aria-hidden="true">
      !
    </span>
  );
}

const FORM: Record<MarkierungArt, { rahmen: string; kopf: string; strich?: string }> = {
  liest: { rahmen: "fill-primary/10 stroke-primary motion-safe:animate-pulse", kopf: "fill-primary", strich: "3 2" },
  fund: { rahmen: "fill-status-warn/20 stroke-status-warn", kopf: "fill-status-warn" },
  gewaehlt: { rahmen: "fill-primary/15 stroke-primary", kopf: "fill-primary" },
  bereich: { rahmen: "fill-status-ok/10 stroke-status-ok", kopf: "fill-status-ok", strich: "3 2" },
  "bereich-ki": { rahmen: "fill-ki/10 stroke-ki", kopf: "fill-ki", strich: "3 2" },
};

function Markierung({ m, hoehe, beiWahl }: { m: DokumentMarkierung; hoehe: number; beiWahl?: (id: string) => void }) {
  const x = m.x * 100;
  const y = m.y * hoehe;
  const b = m.b * 100;
  const h = m.h * hoehe;
  const f = FORM[m.art];
  const bedienbar = beiWahl !== undefined && (m.art === "fund" || m.art === "gewaehlt");
  const rechteck = (
    <rect x={x} y={y} width={b} height={h} rx={0.8} strokeWidth={m.art === "gewaehlt" ? 0.9 : 0.6} strokeDasharray={f.strich} className={f.rahmen} vectorEffect="non-scaling-stroke" />
  );
  const kopfBreite = m.titel ? Math.min(b, 4 + m.titel.length * 1.9) : 0;
  const nummer =
    m.nummer !== undefined ? (
      <g aria-hidden="true">
        <circle cx={x + b} cy={y} r={2.6} className={f.kopf} />
        <text x={x + b} y={y + 1.1} textAnchor="middle" fontSize={3.2} fontWeight={700} className="fill-primary-foreground">
          {m.nummer}
        </text>
      </g>
    ) : null;
  const titel = m.titel ? (
    <g aria-hidden="true">
      <rect x={x} y={y - 5.2} width={kopfBreite} height={5.2} rx={0.8} className={f.kopf} />
      <text x={x + 1.2} y={y - 1.5} fontSize={3} className="fill-primary-foreground">
        {m.titel}
      </text>
    </g>
  ) : null;
  if (!bedienbar) {
    return (
      <g>
        {rechteck}
        {titel}
        {nummer}
      </g>
    );
  }
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={m.label ?? `Fundstelle ${m.nummer ?? ""}`.trim()}
      aria-pressed={m.art === "gewaehlt"}
      className="cursor-pointer focus-visible:outline-2 focus-visible:outline-primary-focus"
      onClick={() => beiWahl(m.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          beiWahl(m.id);
        }
      }}
    >
      {rechteck}
      {nummer}
    </g>
  );
}

export function DokumentAnsicht({
  id,
  dokumente,
  aktivesDokument,
  beiDokument,
  seiten,
  aktiveSeite,
  beiSeite,
  markierungen,
  beiMarkierung,
  kopfRechts,
  satz,
  fuss,
  personenHinweis,
  seiteLeerSatz = "Für diese Seite gibt es kein Seitenbild.",
}: {
  id: string;
  dokumente: DokumentReiter[];
  aktivesDokument: string;
  beiDokument: (id: string) => void;
  seiten: DokumentSeite[];
  aktiveSeite: number;
  beiSeite: (nr: number) => void;
  markierungen: DokumentMarkierung[];
  /** Klick oder Enter auf eine nummerierte Fundstelle. Ohne Angabe sind die Markierungen nur Zeichnung. */
  beiMarkierung?: (id: string) => void;
  /** Rechts im Kopf, neben den Dokument-Reitern („Lokales Bildmodell liest Seite 3“). */
  kopfRechts?: ReactNode;
  /** Satz unter der Seite in Worten; wird als gedrosselte Ansage (`aria-live="polite"`) gelesen. */
  satz?: ReactNode;
  /** Zeile ganz unten (Frist des Seitenbilds). */
  fuss?: ReactNode;
  /** Hinweis, dass Personenbereiche abgedeckt sind (Idee 12); steht unter der Seite, solange es welche gibt. */
  personenHinweis?: string | null;
  seiteLeerSatz?: string;
}) {
  const [zoomIndex, setZoomIndex] = useState(0);
  // Seitenmaße des geladenen Bildes (Höhe bei Breite 100), je Adresse; bis dahin A4.
  const [masse, setMasse] = useState<{ url: string; hoehe: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Anzeigedrehung je Seitenbild (Adresse), 90-Grad-Schritte im Uhrzeigersinn; nur Anzeige, nichts davon wird gespeichert.
  const [drehungen, setDrehungen] = useState<Record<string, AnzeigeDrehung>>({});

  const seite = seiten.find((s) => s.nr === aktiveSeite) ?? seiten[0];
  const index = seite ? seiten.findIndex((s) => s.nr === seite.nr) : -1;
  const url = seite?.bildUrl ?? null;
  const hoehe = masse && masse.url === url ? masse.hoehe : A4_HOEHE;
  const zoom = ZOOM_STUFEN[zoomIndex]!;
  const bildFehlt = url === null || fehler === url;
  const drehung = (url !== null ? drehungen[url] : undefined) ?? 0;
  const quer = drehung === 90 || drehung === 270;

  return (
    <>
      {/* Preline: ecommerce/ecommerce-sub-product-detail-purchase/ecommerce-cat-product-media-galleries/vertical-product-gallery-with-thumbnail-rail */}
      <div className="flex flex-col bg-layer border border-layer-line shadow-xs rounded-xl" data-dokument-ansicht={id}>
        <div className="py-3 px-4 border-b border-layer-line flex flex-wrap items-center justify-between gap-2">
          <div role="tablist" aria-label="Dokumente" className="flex flex-wrap gap-1 p-1 rounded-lg bg-muted">
            {dokumente.map((d) => {
              const aktiv = d.id === aktivesDokument;
              return (
                <button
                  key={d.id}
                  type="button"
                  role="tab"
                  id={`${id}-reiter-${d.id}`}
                  aria-selected={aktiv}
                  onClick={() => beiDokument(d.id)}
                  className={aktiv ? REITER_AKTIV : REITER_INAKTIV}
                >
                  {d.titel}
                  {d.zusatz && <span className="ms-1.5 text-xs font-normal text-muted-foreground-1">{d.zusatz}</span>}
                </button>
              );
            })}
          </div>
          {kopfRechts && <div className="text-xs text-muted-foreground-1">{kopfRechts}</div>}
        </div>

        <div className="p-4 flex gap-4">
          <nav aria-label="Seiten" className="w-16 shrink-0 max-h-192 overflow-y-auto">
            <ol className="space-y-3">
              {seiten.map((s) => {
                const aktiv = seite?.nr === s.nr;
                return (
                  <li key={s.nr}>
                    <button
                      type="button"
                      onClick={() => beiSeite(s.nr)}
                      aria-current={aktiv ? "page" : undefined}
                      aria-label={s.beschriftung}
                      title={s.beschriftung}
                      className="group block w-full text-center focus:outline-hidden"
                    >
                      <span
                        className={aktiv ? MINIATUR_AKTIV : MINIATUR_INAKTIV}
                      >
                        {s.bildUrl ? (
                          <img className="block w-full h-auto" src={s.bildUrl} alt="" loading="lazy" />
                        ) : (
                          <span className="flex items-center justify-center h-20 text-xs text-muted-foreground-1">{s.nr}</span>
                        )}
                        <Stand stand={s.stand} />
                      </span>
                      <span className={aktiv ? SEITENZAHL_AKTIV : SEITENZAHL_INAKTIV}>S. {s.nr}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          <div className="min-w-0 flex-1">
            <div className="rounded-lg border border-layer-line bg-surface p-3 overflow-auto max-h-192">
              {bildFehlt ? (
                <p className="py-16 text-center text-sm text-muted-foreground-1">{seiteLeerSatz}</p>
              ) : (
                <div className={ZOOM_SEITE[zoom]}>
                  {/* Das <img> lädt das Bild, meldet die Maße und trägt den Text „Seite n“ für Hilfstechnik; gezeigt wird dasselbe Bild im SVG. */}
                  <img
                    key={url}
                    className="sr-only"
                    src={url}
                    alt={seite ? `Seite ${seite.nr}` : "Seite"}
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      if (naturalWidth > 0 && naturalHeight > 0) setMasse({ url, hoehe: (naturalHeight / naturalWidth) * 100 });
                    }}
                    onError={() => setFehler(url)}
                  />
                  {/* Anzeigedrehung (Seite drehen): Bild UND Markierungen liegen in EINER Gruppe mit demselben Transform; die Daten bleiben unberührt. */}
                  <svg
                    className="block w-full h-auto"
                    viewBox={`0 0 ${quer ? hoehe : 100} ${quer ? 100 : hoehe}`}
                    preserveAspectRatio="none"
                    role="group"
                    aria-label={`Markierungen auf Seite ${seite?.nr ?? ""}`.trim()}
                    data-drehung={drehung}
                  >
                    <g transform={anzeigeTransform(drehung, hoehe)} data-anzeige-gruppe="">
                      <image href={url} x={0} y={0} width={100} height={hoehe} preserveAspectRatio="none" aria-hidden="true" />
                      {markierungen.map((m) => (
                        <Markierung key={m.id} m={m} hoehe={hoehe} beiWahl={beiMarkierung} />
                      ))}
                    </g>
                  </svg>
                </div>
              )}
            </div>

            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground-1">
              <span className="inline-flex items-center gap-1">
                <Knopf nurSymbol variante="ghost" groesse="xs" label="Vorherige Seite" symbol={<SymbolLinks />} disabled={index <= 0} onClick={() => seiten[index - 1] && beiSeite(seiten[index - 1]!.nr)} />
                <span>
                  Seite {index >= 0 ? index + 1 : 0} von {seiten.length}
                </span>
                <Knopf
                  nurSymbol
                  variante="ghost"
                  groesse="xs"
                  label="Nächste Seite"
                  symbol={<SymbolRechts />}
                  disabled={index < 0 || index >= seiten.length - 1}
                  onClick={() => seiten[index + 1] && beiSeite(seiten[index + 1]!.nr)}
                />
              </span>
              <span className="inline-flex items-center gap-1">
                <Knopf
                  nurSymbol
                  variante="ghost"
                  groesse="xs"
                  label="Seite drehen"
                  symbol={<SymbolDrehen />}
                  disabled={bildFehlt}
                  onClick={() => url !== null && setDrehungen((d) => ({ ...d, [url]: (((d[url] ?? 0) + 90) % 360) as AnzeigeDrehung }))}
                />
                {drehung !== 0 && <span className="tabular-nums">{drehung}°</span>}
                <Knopf nurSymbol variante="ghost" groesse="xs" label="Verkleinern" symbol={<SymbolMinus />} disabled={zoomIndex === 0 || bildFehlt} onClick={() => setZoomIndex((z) => Math.max(0, z - 1))} />
                <span className="tabular-nums" aria-live="polite">
                  {zoom} %
                </span>
                <Knopf
                  nurSymbol
                  variante="ghost"
                  groesse="xs"
                  label="Vergrößern"
                  symbol={<SymbolPlus />}
                  disabled={zoomIndex === ZOOM_STUFEN.length - 1 || bildFehlt}
                  onClick={() => setZoomIndex((z) => Math.min(ZOOM_STUFEN.length - 1, z + 1))}
                />
              </span>
            </div>

            {personenHinweis && <p className="mt-2 text-xs text-muted-foreground-1">{personenHinweis}</p>}
            {satz && (
              <p className="mt-2 text-sm text-foreground" aria-live="polite" aria-atomic="true">
                {satz}
              </p>
            )}
            {fuss && <p className="mt-2 text-xs text-muted-foreground-1">{fuss}</p>}
          </div>
        </div>
      </div>
    </>
  );
}
